import {
  Button,
  Icon,
  Loader,
  Notice,
  Panel,
  RadioGroup,
  TextField,
  Toast
} from '@deque/cauldron-react';
import type { RadioItem } from '@deque/cauldron-react/lib/components/RadioGroup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminAnnouncement,
  AnnouncementAudience,
  AnnouncementInput,
  AnnouncementSeverity
} from '~/common/api-client';
import CopyToClipboard from '~/common/components/CopyToClipboard';
import Separator from '~/common/components/Separator';
import { PRODUCT_SLUGS_WITH_ACCOUNT } from '~/common/constants';
import { useProducts } from '~/common/contexts/products';
import styles from './Announcements.css';
import {
  AUDIENCES,
  PSEUDO_PRODUCT_NAMES,
  SEVERITIES,
  endDateToIso,
  severityToToastType,
  startDateToIso,
  toDateInputValue
} from './announcementsDisplay';
import AnnouncementToastContent from './AnnouncementToastContent';

interface FormValues {
  severity: AnnouncementSeverity;
  product_slug: string;
  audience: AnnouncementAudience;
  description: string;
  link: string;
  link_text: string;
  start_date: string;
  end_date: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>;

const EMPTY: FormValues = {
  severity: 'informational',
  product_slug: '',
  audience: 'all_saas',
  description: '',
  link: '',
  link_text: '',
  start_date: '',
  end_date: ''
};

const fromAnnouncement = (a: AdminAnnouncement): FormValues => ({
  severity: a.severity,
  product_slug: a.product_slug,
  audience: a.audience,
  description: a.description,
  link: a.link ?? '',
  link_text: a.link_text ?? '',
  start_date: toDateInputValue(a.starts_at),
  end_date: toDateInputValue(a.ends_at)
});

interface AnnouncementFormProps {
  mode: 'create' | 'edit';
  initial?: AdminAnnouncement;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (input: AnnouncementInput) => void;
  onCancel: () => void;
}

const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  mode,
  initial,
  submitting = false,
  error = null,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const [values, setValues] = React.useState<FormValues>(
    initial ? fromAnnouncement(initial) : EMPTY
  );
  const [errors, setErrors] = React.useState<FormErrors>({});
  // Bumped on a failed submit so the focus effect re-runs (and re-focuses the
  // error summary) even when the same errors are already displayed.
  const [submitAttempt, setSubmitAttempt] = React.useState(0);
  // Bumped to remount the preview Toast when it is dismissed, so the preview
  // never disappears for good while the author is still editing.
  const [previewNonce, setPreviewNonce] = React.useState(0);
  // A failed save's message lives in the parent's `error` prop (the parent only
  // clears it on the next submit attempt); this lets "Clear form" retire a
  // stale one too. Any change to `error` — a fresh failure — re-surfaces it.
  const [serverErrorCleared, setServerErrorCleared] = React.useState(false);
  const errorSummaryRef = React.useRef<HTMLDivElement>(null);
  const previewFrameRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setServerErrorCleared(false);
  }, [error]);

  // On a failed validation (or a server error) move focus to the summary so
  // assistive-tech users are taken straight to the list of problems.
  React.useEffect(() => {
    errorSummaryRef.current?.focus();
  }, [submitAttempt, error]);

  // Dismissing the preview remounts it (see previewNonce); put focus back on the
  // remounted dismiss control instead of letting it fall to <body> mid-form.
  React.useEffect(() => {
    if (previewNonce === 0) {
      return;
    }
    previewFrameRef.current
      ?.querySelector<HTMLButtonElement>('button.Toast__dismiss')
      ?.focus();
  }, [previewNonce]);

  const update = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues(prev => ({ ...prev, [key]: value }));
    // Clear a field's error as soon as the author starts fixing it.
    setErrors(prev => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const severityLabels: Record<
    AnnouncementSeverity,
    { label: string; description: string }
  > = {
    informational: {
      label: t('Informational'),
      description: t('Events, releases, announcements')
    },
    warning: {
      label: t('Warning notice'),
      description: t(
        'Use for planned, time-bound service impacts only (e.g. scheduled maintenance windows). For active customer-facing issues, use Critical.'
      )
    },
    critical: {
      label: t('Critical notice'),
      description: t(
        'Unplanned outages affecting customers right now. Overuse reduces customer trust in future notices.'
      )
    }
  };

  const audienceLabels: Record<AnnouncementAudience, string> = {
    all_saas: t('All SaaS regions'),
    all_private_cloud: t('All private cloud instances'),
    all_instances: t('All instances (SaaS + private cloud)')
  };

  const severityRadios: RadioItem[] = SEVERITIES.map(severity => ({
    id: `announcement-severity-${severity}`,
    value: severity,
    label: severityLabels[severity].label,
    labelDescription: severityLabels[severity].description
  }));

  const productRadios: RadioItem[] = PRODUCT_SLUGS_WITH_ACCOUNT.map(slug => ({
    slug,
    // Prefer the catalog name, then a friendly pseudo-product name, then the raw
    // slug if the catalog hasn't loaded.
    name: getProductBySlug(slug)?.name ?? PSEUDO_PRODUCT_NAMES[slug] ?? slug
  }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
    .map(({ slug, name }) => ({
      id: `announcement-product-${slug}`,
      value: slug,
      label: name
    }));

  const audienceRadios: RadioItem[] = AUDIENCES.map(audience => ({
    id: `announcement-audience-${audience}`,
    value: audience,
    label: audienceLabels[audience]
  }));

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};
    if (!values.product_slug) {
      nextErrors.product_slug = t('Please select an affected product.');
    }
    if (!values.description.trim()) {
      nextErrors.description = t('Please enter a description.');
    }
    if (!values.start_date) {
      nextErrors.start_date = t('A start date is required.');
    }
    if (!values.end_date) {
      nextErrors.end_date = t('An end date is required.');
    }
    if (
      values.start_date &&
      values.end_date &&
      values.end_date < values.start_date
    ) {
      nextErrors.end_date = t(
        'The end date must be on or after the start date.'
      );
    }
    if (values.link_text.trim() && !values.link.trim()) {
      nextErrors.link = t('Add a link, or remove the link text.');
    }
    return nextErrors;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // The submit button stays focusable while saving (aria-disabled, not
    // disabled), so guard against double submission here.
    if (submitting) {
      return;
    }
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmitAttempt(attempt => attempt + 1);
      return;
    }
    onSubmit({
      severity: values.severity,
      product_slug: values.product_slug,
      audience: values.audience,
      description: values.description.trim(),
      link: values.link.trim() || null,
      link_text: values.link_text.trim() || null,
      starts_at: startDateToIso(values.start_date),
      ends_at: endDateToIso(values.end_date)
    });
  };

  const handleClear = () => {
    setValues(EMPTY);
    setErrors({});
    setServerErrorCleared(true);
  };

  const previewType = severityToToastType(values.severity);
  const title =
    mode === 'edit'
      ? t('Edit toast announcement')
      : t('New toast announcement');
  const errorItems = [
    ...Object.entries(errors).map(([field, message]) => ({
      key: field,
      message
    })),
    ...(error && !serverErrorCleared ? [{ key: 'submit', message: error }] : [])
  ];

  return (
    <Panel padding className={styles.formCard}>
      <h1 className={styles.formTitle}>{title}</h1>
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        {errorItems.length > 0 && (
          <Notice
            ref={errorSummaryRef}
            tabIndex={-1}
            type="caution"
            title={t('Please review the form')}
          >
            {errorItems.length === 1 ? (
              errorItems[0].message
            ) : (
              <ul className={styles.errorList}>
                {errorItems.map(item => (
                  <li key={item.key}>{item.message}</li>
                ))}
              </ul>
            )}
          </Notice>
        )}

        <section className={styles.formSection}>
          <h2
            id="announcement-severity-heading"
            className={styles.formSectionHeading}
          >
            {t('Severity')}
          </h2>
          <RadioGroup
            name="announcement-severity"
            aria-labelledby="announcement-severity-heading"
            value={values.severity}
            radios={severityRadios}
            onChange={(radio: RadioItem) =>
              update('severity', radio.value as AnnouncementSeverity)
            }
          />
        </section>

        <Separator decorative className={styles.formSeparator} />

        <section className={styles.formSection}>
          <h2
            id="announcement-product-heading"
            className={styles.formSectionHeading}
          >
            {t('Affected products')}{' '}
            {/* Visible required indicator matching cauldron's Field markup; the
                group itself carries aria-required for assistive tech. */}
            <span className="Field__required-text" aria-hidden="true">
              {t('Required')}
            </span>
          </h2>
          <RadioGroup
            name="announcement-product"
            aria-labelledby="announcement-product-heading"
            aria-required="true"
            aria-describedby={
              errors.product_slug ? 'announcement-product-error' : undefined
            }
            value={values.product_slug}
            radios={productRadios}
            onChange={(radio: RadioItem) =>
              update('product_slug', radio.value as string)
            }
          />
          {errors.product_slug && (
            <p id="announcement-product-error" className={styles.fieldError}>
              {errors.product_slug}
            </p>
          )}
        </section>

        <Separator decorative className={styles.formSeparator} />

        <section className={styles.formSection}>
          <h2
            id="announcement-audience-heading"
            className={styles.formSectionHeading}
          >
            {t('Audience')}
          </h2>
          <RadioGroup
            name="announcement-audience"
            aria-labelledby="announcement-audience-heading"
            value={values.audience}
            radios={audienceRadios}
            onChange={(radio: RadioItem) =>
              update('audience', radio.value as AnnouncementAudience)
            }
          />
        </section>

        <Separator decorative className={styles.formSeparator} />

        <section className={styles.formSection}>
          <h2 className={styles.formSectionHeading}>{t('Schedule')}</h2>
          <div className={styles.dateRow}>
            <TextField
              label={t('Start date')}
              type="date"
              required
              error={errors.start_date}
              value={values.start_date}
              onChange={(value: string) => update('start_date', value)}
            />
            <TextField
              label={t('End date')}
              type="date"
              required
              error={errors.end_date}
              value={values.end_date}
              onChange={(value: string) => update('end_date', value)}
            />
          </div>
        </section>

        <Separator decorative className={styles.formSeparator} />

        <section className={styles.formSection}>
          <h2 className={styles.formSectionHeading}>
            {t('Announcement content')}
          </h2>
          <TextField
            label={t('Description')}
            required
            multiline
            error={errors.description}
            value={values.description}
            onChange={(value: string) => update('description', value)}
          />
          <TextField
            label={t('Link')}
            type="url"
            error={errors.link}
            value={values.link}
            onChange={(value: string) => update('link', value)}
          />
          <TextField
            label={t('Link text')}
            value={values.link_text}
            onChange={(value: string) => update('link_text', value)}
          />
        </section>

        <Separator decorative className={styles.formSeparator} />

        <section className={styles.formSection}>
          <h2 className={styles.formSectionHeading}>{t('Preview')}</h2>
          <div
            ref={previewFrameRef}
            className={styles.previewFrame}
            data-testid="announcement-preview"
          >
            {/* Not a live region (no role): announcing every keystroke would
                flood screen readers; the inputs themselves are the source of
                truth and the preview can be browsed on demand. */}
            <Toast
              key={previewNonce}
              type={previewType}
              show
              focus={false}
              role={undefined}
              dismissible
              onDismiss={() => setPreviewNonce(nonce => nonce + 1)}
            >
              <AnnouncementToastContent
                severity={values.severity}
                description={
                  values.description || t('Your announcement description')
                }
                link={values.link || null}
                linkText={values.link_text || null}
              />
            </Toast>
          </div>
        </section>

        {mode === 'edit' && initial && (
          <>
            <Separator decorative className={styles.formSeparator} />
            <div className={styles.idField}>
              <span className={styles.idLabel}>{t('Announcement ID')}</span>
              <div className={styles.idRow}>
                <code className={styles.idValue}>{initial.id}</code>
                <CopyToClipboard
                  text={initial.id}
                  confirmationMessage={t('Announcement ID copied to clipboard')}
                >
                  <Button type="button" variant="secondary" thin>
                    <Icon type="copy" />
                    {t('Copy announcement ID')}
                  </Button>
                </CopyToClipboard>
              </div>
            </div>
          </>
        )}

        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={handleClear}>
            {t('Clear form')}
          </Button>
          {/* aria-disabled (not disabled) keeps keyboard focus on the button
              while saving, and the labelled Loader keeps its accessible name. */}
          <Button type="submit" variant="primary" aria-disabled={submitting}>
            {submitting ? <Loader label={t('Saving')} /> : t('Submit')}
          </Button>
          <Button
            type="button"
            variant="link"
            className={styles.cancel}
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
        </div>
      </form>
    </Panel>
  );
};

export default AnnouncementForm;
